<script setup lang="ts">
// 1. Imports
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue';
import { RouteNames } from '@/router/names';
import { usePlanGateStore } from '@/stores/planGate';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

// 2. Local types
type GateContent = {
    title: string;
    message: string;
    cta: string;
    variant: 'primary' | 'warning' | 'danger';
};

// 4. Composables & stores
const { t } = useI18n();
const router = useRouter();
const planGate = usePlanGateStore();
const { error } = storeToRefs(planGate);

// 5. Computed
// Build a targeted message from the 403 meta; the already-localized API `message`
// is the fallback when the meta isn't specific enough (e.g. no required_plan/max).
const content = computed<GateContent | null>(() => {
    const e = error.value;
    if (!e) return null;

    switch (e.code) {
        case 'feature_not_in_plan': {
            const feature = t(`billing.features.${e.feature}`);
            const message = e.required_plan
                ? t('planGate.feature.message', { feature, plan: t(`billing.plans.${e.required_plan}`) })
                : (e.message ?? t('planGate.fallback'));
            return { title: t('planGate.feature.title'), message, cta: t('planGate.feature.cta'), variant: 'primary' };
        }
        case 'limit_reached': {
            const limit = t(`planGate.limitName.${e.limit}`);
            const message =
                e.max !== null
                    ? t('planGate.limit.message', { max: e.max, limit })
                    : (e.message ?? t('planGate.fallback'));
            return { title: t('planGate.limit.title'), message, cta: t('planGate.limit.cta'), variant: 'warning' };
        }
        case 'account_lapsed':
            return {
                title: t('planGate.lapsed.title'),
                message: e.message ?? t('planGate.lapsed.message'),
                cta: t('planGate.lapsed.cta'),
                variant: 'danger',
            };
        // Nothing to buy here: checkout only needs to know which company goes on
        // the VAT invoice, so this gets a choice CTA, not an upgrade one (MILO-184).
        case 'billing_company_required':
            return {
                title: t('planGate.billingCompany.title'),
                message: e.message ?? t('planGate.billingCompany.message'),
                cta: t('planGate.billingCompany.cta'),
                variant: 'warning',
            };
        default:
            return null;
    }
});

// 6. Methods
const onConfirm = (): void => {
    // Read the code before dismissing — dismiss() clears the error. The billing
    // page hosts several sections, so the company gate points at the picker
    // itself instead of dropping the user at the top of the page.
    const query = error.value?.code === 'billing_company_required' ? { section: 'billing-company' } : undefined;

    planGate.dismiss();
    void router.push({ name: RouteNames.Billing, query });
};

const onDismiss = (): void => planGate.dismiss();
</script>

<template>
    <ConfirmDialog
        v-if="content"
        :title="content.title"
        :message="content.message"
        :confirm-text="content.cta"
        :cancel-text="t('planGate.dismiss')"
        :variant="content.variant"
        confirm-variant="primary"
        @confirm="onConfirm"
        @cancel="onDismiss"
    />
</template>
